import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "趣味视频", to: "/category/video" },
  { label: "搞笑段子", to: "/category/joke" },
  { label: "轻松访谈", to: "/category/interview" },
  { label: "关于我们", to: "/about" },
];

function isActive(pathname: string, to: string) {
  if (to === "/") return pathname === "/";
  return pathname.startsWith(to);
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const textBase = solid ? "text-foreground-900" : "text-background-50";
  const linkBase = solid
    ? "text-foreground-700 hover:text-primary-600"
    : "text-background-50/90 hover:text-background-50";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link
          to="/"
          className={`flex items-center gap-2 whitespace-nowrap font-heading font-bold text-lg ${textBase}`}
        >
          <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-emotion-happy-line text-xl" />
          </span>
          羞羞网站
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(location.pathname, item.to)
                  ? "text-primary-600 bg-primary-100/70"
                  : linkBase
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block relative">
            <form onSubmit={submitSearch}>
              <div
                className={`flex items-center gap-2 rounded-full px-3 py-2 transition-all duration-300 ${
                  solid ? "bg-background-100 border border-background-200" : "bg-background-50/20"
                } ${searchOpen ? "w-64" : "w-44"}`}
              >
                <i className={`ri-search-line ${solid ? "text-foreground-500" : "text-background-50"}`} />
                <input
                  value={keyword}
                  onFocus={() => setSearchOpen(true)}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索视频、段子、访谈"
                  className={`bg-transparent outline-none text-sm w-full ${
                    solid ? "text-foreground-900 placeholder:text-foreground-400" : "text-background-50 placeholder:text-background-50/70"
                  }`}
                />
              </div>
            </form>
          </div>

          <button
            type="button"
            aria-label="搜索"
            onClick={() => {
              setMenuOpen(false);
              setSearchOpen((v) => !v);
            }}
            className={`md:hidden w-9 h-9 rounded-md flex items-center justify-center ${
              solid ? "text-foreground-800" : "text-background-50"
            }`}
          >
            <i className="ri-search-line text-xl" />
          </button>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden w-9 h-9 rounded-md flex items-center justify-center ${
              solid ? "text-foreground-800" : "text-background-50"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3">
          <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-full bg-background-100 border border-background-200 px-4 py-2">
            <i className="ri-search-line text-foreground-500" />
            <input
              autoFocus
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入关键词，回车搜索"
              className="bg-transparent outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
            />
            <button type="submit" className="text-primary-600 text-sm font-medium whitespace-nowrap">
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 flex flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-4 py-3 rounded-md text-sm font-medium ${
                isActive(location.pathname, item.to)
                  ? "text-primary-600 bg-primary-100/70"
                  : "text-foreground-700"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}